<template>
  <div>
    <validation-observer
      ref="editCertification"
      #default="{ invalid }"
    >
      <b-form @submit.prevent="submit">
        <b-row>
          <b-col cols="12">
            <validation-provider
              #default="{ errors }"
              name="Rodzaj certyfikacji"
              rules="required"
            >
              <b-form-group
                label="Rodzaj certyfikacji"
                label-for="kind"
              >
                <v-select
                  id="kind"
                  v-model="certificationData.kind"
                  :options="kindOptions"
                  class="w-100"
                  placeholder="Wybierz rodzaj certyfikacji"
                  :reduce="val => val.value"
                  :disabled="!isAdmin"
                />
                <b-form-invalid-feedback :state="errors.length ? false : null">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col cols="12">
            <validation-provider
              #default="{ errors }"
              name="Opis certyfikacji"
              rules="required|max:4000"
            >
              <b-form-group
                label="Opis certyfikacji"
                label-for="description"
              >
                <vue-editor
                  id="description"
                  v-model="certificationData.description"
                  :editor-toolbar="[
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    [{ color: [] }]
                  ]"
                  :class="{ 'is-invalid': errors.length > 0 }"
                />
                <b-form-invalid-feedback :state="errors.length ? false : null">
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Data certyfikacji"
              rules="required"
            >
              <b-form-group
                label="Data certyfikacji"
                label-for="cert-date"
              >
                <b-form-datepicker
                  id="cert-date"
                  v-model="certificationData.date"
                  locale="pl"
                  hide-header
                  label-help=""
                  label-no-date-selected="Nie wybrano daty"
                  start-weekday="1"
                  :state="errors.length > 0 ? false : null"
                />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col cols="12">
            <b-form-group label="Godzina certyfikacji (sloty)">
              <div
                v-for="(slot, index) in certificationData.slotTimeInputs"
                :key="'slot-' + index"
                class="d-flex align-items-center mb-1"
              >
                <b-form-input
                  v-model="slot.time"
                  type="time"
                  class="mr-1"
                  style="max-width: 160px"
                  :disabled="isCertificationSlotOccupied(slot)"
                />
                <b-badge
                  v-if="isCertificationSlotOccupied(slot)"
                  variant="light-primary"
                  class="mr-1 certification-slot-status-badge"
                >
                  Zajęty
                </b-badge>
                <b-button
                  v-if="!isCertificationSlotOccupied(slot) && certificationData.slotTimeInputs.length > 1"
                  variant="outline-danger"
                  size="sm"
                  @click="removeSlot(index)"
                >
                  Usuń
                </b-button>
              </div>
              <b-button
                variant="outline-primary"
                size="sm"
                class="mt-50"
                @click="addSlot"
              >
                Dodaj godzinę
              </b-button>
            </b-form-group>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Dział"
              rules="required"
            >
              <b-form-group
                label="Dział"
                label-for="departments"
              >
                <div
                  v-b-tooltip.hover.v-warning="
                    hasEnrolments
                      ? 'Nie można zmienić działu, gdy są zapisani uczestnicy.'
                      : null
                  "
                >
                  <v-select
                    id="departments"
                    v-model="certificationData.departments"
                    :options="departmentOptions"
                    class="w-100"
                    :reduce="val => val.value"
                    multiple
                    :disabled="hasEnrolments || !isAdmin"
                  />
                </div>
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Egzaminator"
              rules="required"
            >
              <b-form-group
                label="Egzaminator"
                label-for="examiner"
              >
                <v-select
                  id="examiner"
                  v-model="certificationData.examiner"
                  :options="examinerOptions"
                  class="w-100"
                  :reduce="val => val.value"
                  :disabled="!isAdmin"
                />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <validation-provider
              #default="{ errors }"
              name="Lokalizacja"
              rules="required|max:400"
            >
              <b-form-group
                label="Lokalizacja"
                label-for="localization"
              >
                <b-form-input
                  id="localization"
                  v-model="certificationData.localization"
                  :state="errors.length > 0 ? false : null"
                  :disabled="!isAdmin"
                />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <template v-if="isAdmin">
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                v-if="!certificationData.isFree"
                #default="{ errors }"
                name="Koszt"
                rules="required|price|positive-price"
                :custom-messages="{
                  price: 'Wpisz kwotę w formacie x,xx',
                  'positive-price': 'Koszt musi być większy niż 0 zł.',
                }"
              >
                <b-form-group
                  label="Koszt certyfikacji"
                  label-for="priceInGrosze"
                >
                  <b-input-group
                    v-b-tooltip.hover.v-warning="
                      hasEnrolments
                        ? 'Nie można zmienić kosztu, gdy są zapisani uczestnicy.'
                        : null
                    "
                    append="PLN netto / os."
                  >
                    <b-form-input
                      id="priceInGrosze"
                      v-model="price"
                      :state="errors.length > 0 ? false : null"
                      lazy
                      class="text-right"
                      :disabled="hasEnrolments"
                    />
                  </b-input-group>
                  <b-form-invalid-feedback>
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
              <b-form-group
                v-else
                label="Koszt certyfikacji (PLN netto / os.)"
                label-for="price-free"
              >
                <b-form-input
                  id="price-free"
                  value="0,00"
                  disabled
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="4"
              class="d-flex align-items-center"
            >
              <b-form-checkbox
                v-model="isFree"
                class="mt-2"
                :disabled="hasEnrolments || mode === 'edit'"
              >
                Bezpłatna certyfikacja
              </b-form-checkbox>
            </b-col>
            <b-col
              cols="12"
              md="4"
            >
              <validation-provider
                #default="{ errors }"
                name="Status"
                rules="required"
              >
                <b-form-group
                  label="Status"
                  label-for="status"
                >
                  <div
                    v-b-tooltip.hover.v-warning="
                      hasEnrolments
                        ? 'Nie można zmienić statusu, gdy są zapisani uczestnicy.'
                        : null
                    "
                  >
                    <v-select
                      id="status"
                      v-model="certificationData.status"
                      :options="statusOptions"
                      class="w-100"
                      :reduce="val => val.value"
                      :disabled="hasEnrolments"
                    />
                  </div>
                  <b-form-invalid-feedback>
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </template>

          <b-col
            v-if="isAdmin"
            cols="12"
          >
            <validation-provider
              #default="{ errors }"
              name="Dealerzy"
            >
              <b-form-group
                label="Dealerzy"
                label-for="dealers"
              >
                <v-select
                  id="dealers"
                  v-model="certificationData.dealers"
                  :options="dealersOptions"
                  class="w-100"
                  :reduce="val => val.value"
                  multiple
                />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col cols="12">
            <h3>Materiały informacyjne</h3>
            <hr>
            <training-courses-files
              v-model="informationMaterialsFiles"
              :existing-files="
                mode === 'edit' ? certificationData.informationMaterials : null
              "
              :files-to-delete.sync="informationMaterialsToDelete"
              upload
              class="mb-2"
            />
            <h3>Raport</h3>
            <hr>
            <training-courses-files
              v-model="reportMaterialsFiles"
              :existing-files="
                mode === 'edit' ? certificationData.reportMaterials : null
              "
              :files-to-delete.sync="reportMaterialsToDelete"
              upload
              class="mb-2"
            />
          </b-col>
        </b-row>

        <div class="d-sm-flex align-items-center">
          <b-form-checkbox
            v-if="certificationData.status === 'PUBLIC' && isAdmin"
            v-model="sendInformationEmail_c"
            class="mb-1 d-block d-sm-none"
          >
            Powiadom managera
          </b-form-checkbox>

          <b-button
            variant="primary"
            class="mb-1 mb-sm-0 mr-0 mr-sm-1"
            type="submit"
            :disabled="loading || invalid || !!uploading"
          >
            {{ mode === "edit" ? "Zapisz" : "Zapisz" }}
          </b-button>
          <b-button
            variant="outline-secondary"
            to="/certifications-terms/list"
            class="mr-sm-1"
          >
            Anuluj
          </b-button>

          <b-form-checkbox
            v-if="certificationData.status === 'PUBLIC' && isAdmin"
            v-model="sendInformationEmail_c"
            class="d-none d-sm-block flex-shrink-1"
          >
            Powiadom managera
          </b-form-checkbox>
        </div>
      </b-form>
    </validation-observer>

    <b-modal
      ref="uploadingModal"
      hide-header
      hide-footer
      :visible="!!uploading"
      no-close-on-esc
      no-close-on-backdrop
    >
      <h4>Przesyłanie plików...</h4>
      <b-progress
        animated
        :value="uploadProgress"
      />
    </b-modal>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  required,
  maxlength,
  price as priceValidator,
  positivePrice,
} from '@validations'
import departmentOptions from '@/helpers/departmentOptions'
import certificationKindOptions from '@/helpers/certificationKindOptions'
import certificationStatusOptions from '@/helpers/certificationStatusOptions'
import vSelect from 'vue-select'
import {
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BForm,
  BFormInvalidFeedback,
  BFormDatepicker,
  BInputGroup,
  BModal,
  BProgress,
  BFormCheckbox,
  BBadge,
  VBTooltip,
} from 'bootstrap-vue'
import { VueEditor } from 'vue2-editor'
import TrainingCoursesFiles from '@/views/training-courses/TrainingCoursesFiles.vue'
import userRoleMixin from '@/mixins/userRoleMixin'
import {
  formatPriceFromGrosze,
  addCertificationSlot,
  removeCertificationSlot,
  certificationFormHasEnrolments,
  normalizeCertificationDealers,
  ensureDefaultSlotTimes,
  isCertificationSlotOccupied,
  computeMaterialsUploadProgress,
} from '@/helpers/certification/certificationTermsFormLogic'
import {
  buildTrainerSelectOptions,
  buildDealerSelectOptions,
} from '@/helpers/certification/certificationSelectOptions'

export default {
  components: {
    vSelect,
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BFormInvalidFeedback,
    ValidationProvider,
    ValidationObserver,
    BFormDatepicker,
    BInputGroup,
    TrainingCoursesFiles,
    BModal,
    BProgress,
    BFormCheckbox,
    BBadge,
    VueEditor,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [userRoleMixin],
  props: {
    certificationData: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      default: 'new',
    },
    sendInformationEmail: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      required,
      maxlength,
      priceValidator,
      positivePrice,
      departmentOptions,
      kindOptions: certificationKindOptions,
      statusOptions: certificationStatusOptions,
      informationMaterialsFiles: [],
      informationMaterialsToDelete: [],
      reportMaterialsFiles: [],
      reportMaterialsToDelete: [],
      uploading: 0,
    }
  },
  computed: {
    sendInformationEmail_c: {
      get() {
        return this.sendInformationEmail
      },
      set(value) {
        this.$emit('update:sendInformationEmail', value)
      },
    },
    hasEnrolments() {
      return certificationFormHasEnrolments(
        this.mode,
        this.certificationData.takenSlotsCount,
      )
    },
    price: {
      get() {
        return formatPriceFromGrosze(this.certificationData.priceInGrosze)
      },
      set(value) {
        let formatted = parseFloat(String(value).replace(',', '.')).toFixed(2)
        if (formatted === 'NaN') {
          formatted = '0.00'
        }
        this.certificationData.priceInGrosze = Math.round(
          parseFloat(formatted) * 100,
        )
      },
    },
    isFree: {
      get() {
        return this.certificationData.isFree
      },
      set(value) {
        this.certificationData.priceInGrosze = 0
        this.certificationData.isFree = value
      },
    },
    examinerOptions() {
      const users = this.$store.state.user.list
        ? this.$store.state.user.list['hydra:member']
        : []
      return buildTrainerSelectOptions(users)
    },
    dealersOptions() {
      return buildDealerSelectOptions(this.$store.state.dealer.list)
    },
    uploadProgress() {
      return computeMaterialsUploadProgress(
        this.uploading,
        this.informationMaterialsFiles.length,
        this.reportMaterialsFiles.length,
      )
    },
  },
  beforeMount() {
    if (!Array.isArray(this.certificationData.informationMaterials)) {
      this.$set(this.certificationData, 'informationMaterials', [])
    }
    if (!Array.isArray(this.certificationData.reportMaterials)) {
      this.$set(this.certificationData, 'reportMaterials', [])
    }
    this.certificationData.dealers = normalizeCertificationDealers(
      this.certificationData.dealers,
    )
    this.$set(
      this.certificationData,
      'slotTimeInputs',
      ensureDefaultSlotTimes(this.certificationData.slotTimeInputs),
    )
  },
  mounted() {
    Promise.all([
      this.$store.dispatch('user/fetchList', {
        params: { roles: 'ROLE_TRAINER' },
      }),
      this.$store.dispatch('dealer/fetchList'),
    ])
    if (this.mode === 'edit') {
      this.$nextTick(
        () => this.$refs.editCertification
          && this.$refs.editCertification.validate(),
      )
    }
  },
  methods: {
    isCertificationSlotOccupied,
    addSlot() {
      this.certificationData.slotTimeInputs = addCertificationSlot(
        this.certificationData.slotTimeInputs,
      )
    },
    removeSlot(index) {
      this.certificationData.slotTimeInputs = removeCertificationSlot(
        this.certificationData.slotTimeInputs,
        index,
      )
    },
    uploadFiles() {
      this.uploading = this.reportMaterialsFiles.length
        + this.informationMaterialsFiles.length
      const uploadNext = file => {
        const formdata = new FormData()
        formdata.append('file', file)
        return new Promise(resolve => {
          this.$http
            .post('media_objects', formdata)
            .then(response => {
              if (response && response.status === 201) {
                resolve(response)
              } else {
                resolve(response)
              }
            })
            .catch(error => resolve(error))
        })
      }
      const uploadStack = (files, destination) => {
        const stack = []
        files.forEach(file => {
          stack.push(
            uploadNext(file)
              .then(response => {
                this.uploading -= 1
                if (response && response.status === 201) {
                  destination.push(response.data['@id'])
                }
              })
              .catch(() => {
                this.uploading = 0
              }),
          )
        })
        return Promise.all(stack)
      }
      return new Promise(resolve => {
        if (
          !this.informationMaterialsFiles.length
          && !this.reportMaterialsFiles.length
        ) {
          resolve()
        } else {
          Promise.all([
            uploadStack(
              this.reportMaterialsFiles,
              this.certificationData.reportMaterials,
            ),
            uploadStack(
              this.informationMaterialsFiles,
              this.certificationData.informationMaterials,
            ),
          ]).then(() => resolve())
        }
      })
    },
    deleteFiles() {
      this.reportMaterialsToDelete.forEach(fileToDelete => {
        this.certificationData.reportMaterials = this.certificationData.reportMaterials.filter(
          file => file['@id'] !== fileToDelete['@id'],
        )
      })
      this.informationMaterialsToDelete.forEach(fileToDelete => {
        this.certificationData.informationMaterials = this.certificationData.informationMaterials.filter(
          file => file['@id'] !== fileToDelete['@id'],
        )
      })
    },
    submit() {
      this.$refs.editCertification.validate().then(valid => {
        if (!valid) return
        if (this.mode === 'edit') this.deleteFiles()
        this.uploadFiles().then(() => {
          this.$emit('submit')
        })
      })
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.certification-slot-status-badge {
  font-size: 0.857rem;
  font-weight: 500;
  padding: 0.486rem 0.9rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.quillWrapper .ql-toolbar {
  border-top-left-radius: 0.357rem;
  border-top-right-radius: 0.357rem;
}
.quillWrapper .ql-container {
  border-bottom-left-radius: 0.357rem;
  border-bottom-right-radius: 0.357rem;
}
</style>
